import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';
import {
    Instrument,
    LoanInstrumentData
} from '../../../shared/state/instruments/instrument.model';
import { AccountTypeEnum, Acct } from '../../../shared/state/accts/acct.model';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { AccountIssuer } from '../../../pages/admin/state/account-issuers/account-issuer.model';
import {
    ExtendedPositionPerformance,
    PeriodPositionPerformance,
    PositionPerformance
} from './performance.model';
import { CurrenciesAndRates } from '../../settings/state/settings.store';

export interface Position {
    id: number;
    valueDate: string;
    valueDateTime: string;
    aggregatedAtDateTime: string;
    position: {
        positionId: number;
        previousPositionId: number;
        nextPositionId: number;
        quantity: number;
        settledQuantity: number;
        currency: Currency;
        acquisitionPriceAvg: number;
        acquisitionPriceFifo: number;
        totalCommission: number;
        totalDividend: number;
        realizedQuantity: number;
        realizedPnLAvg: number;
        realizedPnLFifo: number;
        realizedAcquisitionPriceAvg: number;
        realizedAcquisitionPriceFifo: number;
        totalInvestedAmount: number;
        totalBoughtSoldNetAmount: number;
        totalCashFlows: number;
        updatingTransactionId: number;
        updatingTransactionPrice: number;
        positionQuantityType: string;
    };
    instrument: Partial<Instrument>;
    instrumentData?: LoanInstrumentData; //included in the response of Loans endpoints
    account: {
        ownerId: Acct['ownerId'];
        accountId: Acct['accountId'];
        accountType: Acct['accountType']; // @deprecated look up using account id instead as the account information on the position level is stale, future versions will only hold reference to the account ID;
    };
    owner: any;
    valuations: any;
    classifications: any;
    instrumentName: string;
    priceDate: string;
    marketPrice: number;
    marketValue: number;
    cashInBaseCurrency: number; // is instrument.type === 'CASH' ? marketValueInBaseCurrency : 0 calculated when added to store
    acquisitionValue: number; // acquisitionPriceAvg * quantity calculated when added to store
    unrealizedPnLAvg: number;
    unrealizedPnLFifo: number;
    relativeUnrealizedPnLAvg: number;
    relativeUnrealizedPnLFifo: number;
    basePriceFxRate: number;
    fxRateQuoteDate: string;
    dailyPerformanceRelative: number;
    dailyPerformanceNonDividendAdjustedRelative: number;
    ownership: any;
    dilutedOwnership: number;
    marketValueInBaseCurrency: number;
    multipleOfInvestedCapital: number;
}

export interface ExtendedPosition extends Position {
    acquisitionValueInBaseCurrency: number;
    totalPnlInBaseCurrency: number;
    groupBy: {
        legalEntity: LegalEntity;
        accountIssuer: AccountIssuer;
        account: Acct;
        instrument: Instrument;
        positionType: Instrument['positionType'];
    };
}

export const extendPosition = (
    entity: Position,
    account: Acct,
    accountIssuer: AccountIssuer,
    legalEntity: LegalEntity,
    instrument: Instrument,
    currenciesAndRates: CurrenciesAndRates
): ExtendedPosition => {
    // determine position type based on market value for position
    let positionType: Instrument['positionType'] =
        entity.marketValueInBaseCurrency < 0 ? 'LIABILITY' : 'ASSET';
    // determine instrument type for loans
    if (instrument?.type === 'LOAN') {
        if (positionType === 'ASSET') {
            instrument = {
                ...instrument,
                instrumentType: 'LOANS_RECEIVABLE'
            };
        } else {
            instrument = {
                ...instrument,
                instrumentType: 'LOANS_PAYABLE'
            };
        }
    }
    const conversionRate = currenciesAndRates[entity.position.currency];
    const acquisitionValueInBaseCurrency = entity.acquisitionValue * conversionRate;
    const totalPnlInBaseCurrency =
        ((entity.unrealizedPnLAvg || 0) + (entity.position.realizedPnLAvg || 0)) *
        conversionRate;

    return {
        ...entity,
        acquisitionValueInBaseCurrency,
        totalPnlInBaseCurrency,
        groupBy: {
            legalEntity,
            accountIssuer,
            account,
            instrument,
            positionType
        }
    };
};

export interface AccumulatedPosition {
    totalPnlInBaseCurrency: number;
    totalCommission: number;
    totalDividend: number;
    realizedQuantity: number;
    marketValueInBaseCurrency: number;
    acquisitionValueInBaseCurrency: number;
    cashInBaseCurrency: number;
}

export interface AccumulatedPositionWithWeights {
    marketValueInPercent: number;
    allocationInPercent: number;
    allocationPercentagePoints: number;
    marketValueAllocation: number;
}

export function createPosition(params: Partial<Position>) {
    return {} as Position;
}
